import { hasOwnProperty, withDefault } from "collection-utils";
import deepEqual from "deep-equal";
import { isLeft } from "fp-ts/lib/Either";
import * as iots from "io-ts";
import type { Mutable } from "utility-types";
import type { LocalizedStringKey } from "../common/components/localization";
import type { NativeTableName, TableName, UniversalTableName } from "../common/database-strings";
import {
    areTableNamesEqual,
    isNativeTableName,
    isTableName,
    nativeTableNameCodec,
    tableNameCodec,
} from "../common/database-strings";
import { isFavoritedColumnName } from "../common/google-sheets";
import { memoizeFunction } from "../common/memoize";
import { assert, assertNever, isArray, panic } from "../support";
import { makeComponentID } from "../support/component-id";
import type { NativeTableSourceMetadata, SourceMetadata } from "./source-metadata";
import { getSourceMetadataForNativeTable, nativeTableSourceMetadata } from "./source-metadata";

const stringGlideTypeKindRecord = {
    string: null,
    uri: null,
    "image-uri": null,
    "audio-uri": null,
    date: null,
    time: null,
    "date-time": null,
    markdown: null,
    "phone-number": null,
    "email-address": null,
    emoji: null,
};
export const stringGlideTypeKind = iots.keyof(stringGlideTypeKindRecord);
export type StringGlideTypeKind = iots.TypeOf<typeof stringGlideTypeKind>;

const nonStringPrimitiveGlideTypeKindRecord = {
    number: null,
    duration: null,
    boolean: null,
};
const primitiveGlideTypeKind = iots.union([stringGlideTypeKind, iots.keyof(nonStringPrimitiveGlideTypeKindRecord)]);
export type PrimitiveGlideTypeKind = iots.TypeOf<typeof primitiveGlideTypeKind>;

export const primitiveTypeKinds = [
    ...Object.keys(stringGlideTypeKindRecord),
    ...Object.keys(nonStringPrimitiveGlideTypeKindRecord),
] as readonly PrimitiveGlideTypeKind[];

export function isLinkTypeKind(kind: ColumnTypeKind | undefined): kind is "uri" | "image-uri" | "audio-uri" {
    return kind !== undefined && ["uri", "image-uri", "audio-uri"].indexOf(kind) >= 0;
}

export function isDateOrDateTimeTypeKind(kind: ColumnTypeKind): kind is "date" | "date-time" {
    return kind === "date" || kind === "date-time";
}

export function isDateTimeTypeKind(kind: ColumnTypeKind): kind is "date" | "time" | "date-time" {
    return isDateOrDateTimeTypeKind(kind) || kind === "time";
}

export function isStringTypeKind(kind: ColumnTypeKind | undefined): kind is StringGlideTypeKind {
    if (kind === undefined) return false;
    return (
        ["string", "date", "time", "date-time", "markdown", "phone-number", "email-address", "emoji"].indexOf(kind) >=
            0 || isLinkTypeKind(kind)
    );
}

export function isNumberTypeKind(t: ColumnTypeKind): t is "number" | "duration" {
    return t === "number" || t === "duration";
}

export function isPrimitiveTypeKind(kind: ColumnTypeKind): kind is PrimitiveGlideTypeKind {
    if (isStringTypeKind(kind)) return true;
    return ["number", "duration", "boolean"].indexOf(kind) >= 0;
}

export function isPrimitiveType(t: ColumnType | undefined): t is PrimitiveGlideType {
    if (t === undefined) return false;
    return isPrimitiveTypeKind(t.kind);
}

export function isStringTypeOrStringTypeArray(t: ColumnType): boolean {
    return isStringTypeKind(t.kind) || (t.kind === "array" && isStringTypeKind(t.items.kind));
}

export function isSingleRelationType(t: ColumnType): t is UniversalTableRefGlideType {
    return t.kind === "table-ref";
}

export type MultiRelationType = ArrayColumnType & { items: TableRefGlideType };

export function isMultiRelationType(t: ColumnType): t is MultiRelationType {
    return t.kind === "array" && isSingleRelationType(t.items);
}

// This can't be a type assertion until TS fixes its bug.
// https://github.com/quicktype/glide/issues/7928
export function isRelationType(t: ColumnType): boolean {
    return isSingleRelationType(t) || isMultiRelationType(t);
}

export function decomposeRelationType(
    t: ColumnType
): { tableRef: UniversalTableRefGlideType; isMulti: boolean } | undefined {
    if (isSingleRelationType(t)) {
        return { tableRef: t, isMulti: false };
    } else if (isMultiRelationType(t)) {
        return { tableRef: t.items, isMulti: true };
    } else {
        return undefined;
    }
}

const primitiveGlideType = iots.type({
    kind: primitiveGlideTypeKind,
});
export type PrimitiveGlideType = iots.TypeOf<typeof primitiveGlideType>;

const tableRefGlideType = iots.intersection([
    iots.type({
        kind: iots.literal("table-ref"),
        tableName: iots.union([iots.string, tableNameCodec]),
    }),
    iots.partial({
        // FIXME: This is an odd place for the target column name in a reference.
        // It's not technically part of the type, and especially with the new
        // references we don't even need it anymore.  Once we get rid of the old
        // reference objects this should be removed.
        refColumnName: iots.string,
    }),
]);
export type TableRefGlideType = iots.TypeOf<typeof tableRefGlideType>;

const nativeTableRefGlideType = iots.intersection([
    iots.type({
        kind: iots.literal("table-ref"),
        tableName: nativeTableNameCodec,
    }),
    iots.partial({
        // For ease of use
        refColumnName: iots.undefined,
    }),
]);
export type NativeTableRefGlideType = iots.TypeOf<typeof nativeTableRefGlideType>;

const universalTableRefGlideType = iots.union([tableRefGlideType, nativeTableRefGlideType]);
export type UniversalTableRefGlideType = iots.TypeOf<typeof universalTableRefGlideType>;

export function getTableRefTableName(nameOrRef: TableName | TableRefGlideType): TableName;
export function getTableRefTableName(nameOrRef: UniversalTableName | UniversalTableRefGlideType): UniversalTableName;
export function getTableRefTableName(nameOrRef: UniversalTableName | UniversalTableRefGlideType): UniversalTableName {
    if (hasOwnProperty(nameOrRef, "isSpecial")) {
        return nameOrRef;
    }

    const { tableName } = nameOrRef;
    if (typeof tableName === "string") {
        return makeTableName(tableName);
    }
    return tableName;
}

const arrayItemType = iots.union([primitiveGlideType, universalTableRefGlideType]);
export type ArrayItemType = iots.TypeOf<typeof arrayItemType>;

const arrayColumnType = iots.intersection([
    iots.type({
        kind: iots.literal("array"),
        items: arrayItemType,
    }),
    iots.partial({
        itemColumnNames: iots.readonlyArray(iots.string),
    }),
]);

export type ArrayColumnType = iots.TypeOf<typeof arrayColumnType>;

export function makeArrayType(items: ArrayItemType, itemColumnNames?: readonly string[]): ArrayColumnType {
    const t = { kind: "array", items } as const;
    if (itemColumnNames === undefined) {
        return t;
    }
    return { ...t, itemColumnNames };
}

export type ColumnType = PrimitiveGlideType | ArrayColumnType | UniversalTableRefGlideType;

const columnType: iots.Type<ColumnType> = iots.union([primitiveGlideType, arrayColumnType, universalTableRefGlideType]);

export enum SourceColumnKind {
    DefaultContext = "default-ctx",
    UserProfile = "user-profile",
    ContainingScreen = "containing-screen",
}

export interface SourceColumn {
    readonly kind: SourceColumnKind;
    readonly name: string | readonly string[];
}
export type LegacySourceColumn = string | SourceColumn;

export function isSourceColumn(x: unknown): x is SourceColumn {
    if (!hasOwnProperty(x, "kind")) return false;
    if (
        x.kind !== SourceColumnKind.DefaultContext &&
        x.kind !== SourceColumnKind.UserProfile &&
        x.kind !== SourceColumnKind.ContainingScreen
    ) {
        return false;
    }
    if (!hasOwnProperty(x, "name")) return false;
    if (!isArray(x.name) && typeof x.name !== "string") return false;
    return true;
}

export function areSourceColumnsEqual(a: SourceColumn, b: SourceColumn): boolean {
    return deepEqual(a, b, { strict: true });
}

export function makeSourceColumn(name: string, kind?: SourceColumnKind): SourceColumn;
export function makeSourceColumn(sourceColumn: LegacySourceColumn): SourceColumn;
export function makeSourceColumn(value: unknown): SourceColumn | undefined;
export function makeSourceColumn(name: unknown, kind?: SourceColumnKind): SourceColumn | undefined {
    if (isSourceColumn(name)) {
        assert(kind === undefined);
        return name;
    } else if (typeof name === "string") {
        return { kind: kind ?? SourceColumnKind.DefaultContext, name };
    } else {
        return undefined;
    }
}

export function getSourceColumnPath({ name }: SourceColumn): readonly string[] {
    if (isArray(name)) {
        return name;
    } else {
        return [name];
    }
}

export function getSourceColumnSinglePath(sc: SourceColumn): string {
    const path = getSourceColumnPath(sc);
    assert(path.length === 1);
    return path[0];
}

export function getDefaultContextColumnName(sc: SourceColumn): string | undefined {
    if (sc.kind !== SourceColumnKind.DefaultContext) return undefined;
    const path = getSourceColumnPath(sc);
    if (path.length !== 1) return undefined;
    return path[0];
}

export type ColumnTypeKind = PrimitiveGlideTypeKind | "array" | "table-ref";

export enum FormulaKind {
    GetTableRows = "get-table-rows",
    FilterRows = "filter-rows",
    MapRows = "map-rows",
    Reduce = "reduce",
    ReduceToMemberBy = "reduce-to-member-by",
    JoinStrings = "join-strings",
    SplitString = "split-string",
    FindRow = "find-row",
    IfThenElse = "if-then-else",
    CheckValue = "check-value",
    CompareValues = "compare-values",
    Constant = "constant",
    Empty = "empty",
    GetContext = "get-context",
    GetColumn = "get-column",
    GetUserProfileRow = "get-user-profile-row",
    ApplyColumnFormat = "apply-column-format",
    TextTemplate = "text-template",
    ArrayContains = "array-contains",
    ArraysOverlap = "arrays-overlap",
    MakeArray = "make-array",
    And = "and",
    Or = "or",
    Not = "not",
    SpecialValue = "special-value",
    ConvertToType = "convert-to-type",
    StartOfDay = "start-of-day",
    EndOfDay = "end-of-day",
    With = "with",
    GetNth = "get-nth",
    GetNthLast = "get-nth-last",
    RandomPick = "random-pick",
    IsInRange = "is-in-range",
    AssignVariables = "assign-variables",
    GetVariable = "get-variable",
    Random = "random",
    UnaryMath = "unary-math",
    BinaryMath = "binary-math",
    WithUserEnteredText = "with-user-entered-text",
    FormatNumberFixed = "format-number-fixed",
    FormatDateTime = "format-date-time",
    FormatDuration = "format-duration",
    CurrentLocation = "current-location",
    GeoDistance = "geo-distance",
    GeocodeAddress = "geocode-address",
    GenerateImage = "generate-image",
    UserAPIFetch = "user-api-fetch",
    ConstructURL = "construct-url",
    YesCode = "yes-code",
}

export interface Formula {
    readonly kind: FormulaKind;
}

// FIXME: implement validation
const formulaCodec = new iots.Type<Formula>(
    "Formula",
    (_u): _u is Formula => true,
    i => iots.success(i as Formula),
    a => a
);

const tableColumnIdentityCodec = iots.intersection([
    iots.type({ name: iots.string }),
    iots.partial({ displayName: iots.string }),
]);

export type TableColumnIdentity = iots.TypeOf<typeof tableColumnIdentityCodec>;

export interface TableColumn extends TableColumnIdentity {
    readonly type: ColumnType;

    // Hidden columns are not shown in column selectors for component
    // properties. As of now, the row index is hidden, as well as the user
    // specific favorite flag.  We also mark protected columns as hidden, but
    // we still show them in the Data Editor.
    readonly hidden?: boolean;
    // Protected columns are not mirrored by Glide.  We also mark them as
    // hidden. Only sheet columns can be protected.
    readonly isProtected?: boolean;
    readonly isUserSpecific?: boolean;
    readonly formula?: Formula;
    readonly displayFormula?: Formula;
    // Columns that are copied from native tables.  Note that this might not
    // be set for data columns, but it must be set for copied computed
    // columns.
    readonly fromNativeTable?: boolean;
    // Columns that are copied from external sources.  Note that this might
    // not be set for data columns, but it must be set for copied computed
    // columns.
    readonly fromExternalSource?: boolean;
    // ##readOnlyData:
    // Defaults to `false`.  Computed columns are implicitly read-only and
    // might not have this flag set.  We use read-only columns when importing
    // from external sources that expose computed columns on their end.
    readonly isReadOnly?: boolean;
}

export const tableColumnCodec: iots.Type<TableColumn> = iots.intersection([
    tableColumnIdentityCodec,
    iots.type({
        type: columnType,
    }),
    iots.partial({
        hidden: iots.boolean,
        isProtected: iots.boolean,
        isUserSpecific: iots.boolean,
        formula: formulaCodec,
        displayFormula: formulaCodec,
        fromNativeTable: iots.boolean,
    }),
]);

export function isComputedColumn(c: TableColumn): c is TableColumn & { readonly formula: Formula } {
    return c.formula !== undefined;
}

export function isUserAgnosticDataColumn(c: TableColumn): boolean {
    if (isComputedColumn(c)) return false;
    if (c.isUserSpecific === true) return false;
    return true;
}

export function isDataSourceColumn(c: TableColumn, isNative: boolean): boolean {
    if (isComputedColumn(c)) return false;
    if (c.isUserSpecific === true && !isNative) return false;
    return true;
}

export function getTableColumnDisplayName(c: TableColumn): string {
    return withDefault(c.displayName, c.name);
}

export function canBeRowIDColumn(tableOrColumns: TableGlideType | readonly TableColumn[], columnName: string): boolean {
    const column = getTableColumn(tableOrColumns, columnName);
    if (column === undefined) return false;
    return isUserAgnosticDataColumn(column) && isPrimitiveType(column.type);
}

export interface TableGlideType {
    readonly name: string | TableName;
    readonly columns: readonly TableColumn[];

    readonly sheetID?: number;
    readonly sheetName?: string;
    // This might not be accurate.  It's mainly used on refresh to
    // determine whether to make a class screen vs array screen, when
    // there is exactly one data row.
    readonly numDataRows?: number;
    readonly emailOwnersColumn?: string | readonly string[];
    readonly rowIDColumn?: string;
    readonly sourceMetadata?: NativeTableSourceMetadata;
    // Defaults to `false`.  External sources with ##readOnlyData will have
    // this set.  It means that rows can't be added or deleted, and that all
    // columns are implicitly read-only.
    readonly isReadOnly?: boolean;
}

// FIXME: It completely sucks that this exists independent of TableGlideType.
// This needs to _be_ TableGlideType.
export interface TableGlideTypeWithUniversalName {
    readonly name: string | UniversalTableName;
    readonly columns: readonly TableColumn[];

    readonly sheetName?: string;
    readonly rowIDColumn?: string;
    readonly isReadOnly?: boolean;
    // FIXME: Add sourceMetadata at some point
}

export const tableGlideTypeCodec: iots.Type<TableGlideType> = iots.intersection([
    iots.type({
        name: iots.union([iots.string, tableNameCodec]),
        columns: iots.readonlyArray(tableColumnCodec),
    }),
    iots.partial({
        sheetID: iots.number,
        sheetName: iots.string,
        numDataRows: iots.number,
        emailOwnersColumn: iots.union([iots.string, iots.readonlyArray(iots.string)]),
        rowIDColumn: iots.string,
        sourceMetadata: nativeTableSourceMetadata,
        isReadOnly: iots.boolean,
    }),
]);

export interface TableOrColumnsAndColumn {
    readonly table: readonly TableColumn[] | TableGlideType;
    readonly column: TableColumn;
}

export interface TableAndColumn extends TableOrColumnsAndColumn {
    readonly table: TableGlideType;
}

export function getEmailOwnersColumnNames(table: TableGlideType, expandArray: boolean = false): readonly string[] {
    const { emailOwnersColumn } = table;
    if (emailOwnersColumn === undefined) return [];
    let columnNames: readonly string[];
    if (typeof emailOwnersColumn === "string") {
        columnNames = [emailOwnersColumn];
    } else {
        columnNames = emailOwnersColumn;
    }
    if (!expandArray) return columnNames;
    const expanded: string[] = [];
    for (const n of columnNames) {
        const c = getTableColumn(table, n);
        if (c === undefined) continue;
        if (c.type.kind === "array" && c.type.itemColumnNames !== undefined) {
            expanded.push(...c.type.itemColumnNames);
        }
    }
    return [...columnNames, ...expanded];
}

export function tableHasRowOwners(table: TableGlideType): boolean {
    return getEmailOwnersColumnNames(table).length > 0;
}

export function sheetNameForTable(table: TableGlideType): string {
    return withDefault(table.sheetName, getTableName(table).name);
}

export interface InputOutputTables {
    readonly input: TableGlideType;
    readonly output: TableGlideType;
}

export const makeInputOutputTables = memoizeFunction(
    "makeInputOutputTables",
    (input: TableGlideType, output?: TableGlideType): InputOutputTables => {
        return { input, output: output ?? input };
    }
);

export function withOutputTable(tables: InputOutputTables, output: TableGlideType): InputOutputTables {
    return makeInputOutputTables(tables.input, output);
}

export function getInputOrOutputTable(tables: InputOutputTables, isEdit: boolean): TableGlideType {
    if (isEdit) {
        return tables.output;
    } else {
        return tables.input;
    }
}

// The following two items need to be kept in sync.
// We'd say
//   type TypeSchema = iots.TypeOf<typeof typeSchemaCodec>
// but then TypeSchema["tables"] would be mutable, both in terms of
// the property and the underlying array.
export interface TypeSchema {
    readonly tables: ReadonlyArray<TableGlideType>;
}

export const typeSchemaCodec = iots.type({
    tables: iots.readonlyArray(tableGlideTypeCodec),
});

export function makeTypeSchema(tables: readonly TableGlideType[]): TypeSchema {
    return { tables };
}

export function decodeTypeSchema(x: unknown): TypeSchema | undefined {
    const decoded = typeSchemaCodec.decode(x);
    return isLeft(decoded) ? undefined : makeTypeSchema(decoded.right.tables);
}

export function isolateTypeSchema(schema: TypeSchema): TypeSchema {
    return makeTypeSchema(schema.tables);
}

export function makeTableName(name: string | TableName): TableName;
export function makeTableName(name: string, isSpecial: boolean): TableName;
export function makeTableName(name: string | TableName, isSpecial: boolean = false): TableName {
    if (typeof name !== "string") return name;
    return { name, isSpecial };
}

export function getTableName(tableOrTableName: TableGlideType | TableName): TableName {
    if (hasOwnProperty(tableOrTableName, "columns")) {
        return makeTableName(tableOrTableName.name);
    } else {
        return tableOrTableName;
    }
}

export enum ImageKind {
    URL = "url",
    MapFromAddress = "map-from-address",
}

export enum PropertyKind {
    Column = "column",
    Table = "table",
    Enum = "enum",
    String = "string",
    Number = "number",
    Switch = "switch",
    Constant = "constant",
    SpecialValue = "special-value",
    PaymentMethod = "payment-method",
    Screen = "screen",
    Filter = "filter",
    Formula = "formula",
    Transforms = "transforms",
    Sorts = "sorts",
    Array = "array",
    Zap = "zap",
    Webhook = "webhook",
    Icon = "icon",
    Emoji = "emoji",
    Action = "action",
    CompoundAction = "compound-action",
}

export interface Description {}

export interface PropertyDescription {
    readonly kind: PropertyKind;
    readonly value: unknown;
}

export type LegacyPropertyDescription = PropertyDescription | unknown;

export interface AppUpdater {
    readonly appID: string;
    setFreeScreenTable(
        screenName: string,
        fetchesData: boolean,
        isDetailScreen: boolean | undefined,
        tableRef: TableRefGlideType,
        rewriteScreen: boolean,
        isRootScreen: boolean
    ): void;
    demandRowIDColumn(tableName: TableName, explanation: string): void;
}

export type PropertySource =
    | {
          readonly name: string;
          readonly defaultValue?: PropertyDescription;
      }
    | {
          readonly id: string;
          // NOTE: This is almost certainly not what you want - use the `name`
          // case above.
          readonly get: (desc: Description) => LegacyPropertyDescription | undefined;
          readonly update: (
              desc: Description,
              value: PropertyDescription | undefined,
              updater: AppUpdater | undefined
          ) => Partial<Description>;
      };

export function arePropertySourcesEqual(s1: PropertySource, s2: PropertySource): boolean {
    if (hasOwnProperty(s1, "name")) {
        if (hasOwnProperty(s2, "name")) {
            return s1.name === s2.name;
        } else {
            return false;
        }
    } else {
        if (hasOwnProperty(s2, "name")) {
            return false;
        } else {
            return s1.id === s2.id;
        }
    }
}

export interface ImageDescription {
    readonly imageKind: LegacyPropertyDescription;
    readonly imageURLProperty: LegacyPropertyDescription;
}

export interface TitleDescription extends Partial<ImageDescription> {
    readonly titleProperty?: LegacyPropertyDescription;
    readonly subtitleProperty?: LegacyPropertyDescription;
}

export function takeValuesFromSummaryDescription<T extends TitleDescription>(desc: T, summary: TitleDescription): T {
    if (summary.titleProperty !== undefined) {
        desc = { ...desc, titleProperty: summary.titleProperty };
    }
    if (summary.subtitleProperty !== undefined) {
        desc = { ...desc, subtitleProperty: summary.subtitleProperty };
    }
    if (summary.imageURLProperty !== undefined) {
        desc = { ...desc, imageURLProperty: summary.imageURLProperty };
    }
    if (summary.imageKind !== undefined) {
        desc = { ...desc, imageKind: summary.imageKind };
    }
    return desc;
}

export function isolateSummaryDescription(summary: TitleDescription): TitleDescription {
    const empty: TitleDescription = {
        titleProperty: undefined,
        subtitleProperty: undefined,
        imageURLProperty: undefined,
    };
    return takeValuesFromSummaryDescription(empty, summary);
}

export enum ActionKind {
    OpenLink = "open-link",
    OpenLinkWithArgs = "open-link-with-args",
    OpenWebView = "open-web-view",
    PhoneCall = "phone-call",
    PhoneCallWithArgs = "phone-call-with-args",
    TextMessage = "text-message",
    TextMessageWithArgs = "text-message-with-args",
    SendEmail = "send-email",
    SendEmailWithArgs = "send-email-with-args",
    Alert = "alert",
    CopyToClipboard = "copy-to-clipboard",
    CopyToClipboardWithArgs = "copy-to-clipboard-with-args",
    OpenMap = "open-map",
    OpenMapWithArgs = "open-map-with-args",
    AddRow = "add-row",
    SetColumns = "set-columns",
    DeleteRow = "delete-row",
    EnlargeImage = "enlarge-image",
    UploadImage = "upload-image",
    FormScreen = "form-screen",
    PushFreeScreen = "push-free-screen",
    ShowShareSheet = "show-share-sheet",
    ShowShareSheetWithArgs = "show-share-sheet-with-args",
    PushDetailScreen = "push-detail-screen",
    PushEditScreen = "push-edit-screen",
    Zapier = "zapier",
    Reshuffle = "reshuffle",
    Increment = "Increment",
    SignIn = "sign-in",
    PopScreen = "pop-screen",
    CloseModal = "close-modal",
    NavigateToTab = "navigate-to-tab",
    ShowToast = "show-toast",
    ScanCode = "scan-code",
    LiveScanLinearBarcode = "live-scan-linear-barcode",
    PlaySound = "play-sound",
    Compound = "compound",
    DeliverEmail = "deliver-email",
    Webhook = "webhook",
    PushUserProfileScreen = "push-user-profile-screen",
}

export interface ActionDescription {
    readonly kind: ActionKind;
}

export type ComponentKind = string;

export interface HideableDescription {
    readonly visibilityFilters: readonly ArrayTransform[] | undefined;
}

// [span, column in span, component in column]
export type SpanCoordsFloat = [number, number, number];

export interface ComponentDescription extends HideableDescription {
    readonly kind: ComponentKind;
    readonly builderDisplayName?: string;

    // NOTE: Do not depend on this being unique or particularly stable.
    // Also see ##removeComponentIDsFromAppDescription.
    readonly componentID: string;

    // This is for the 2D layout prototype
    readonly spanCoordsFloat?: SpanCoordsFloat;
}

export function makeEmptyComponentDescription(kind: ComponentKind): ComponentDescription {
    return { kind, componentID: makeComponentID(), visibilityFilters: undefined };
}

export interface DefaultableComponentDescription extends ComponentDescription {
    readonly propertyName: PropertyDescription;
    readonly defaultValue: PropertyDescription | undefined;
}

export interface DescriptionWithAction {
    readonly actions: LegacyPropertyDescription | undefined;
}

export interface ActionComponentDescription extends ComponentDescription, DescriptionWithAction {}

export interface ContainerComponentDescription extends ComponentDescription {
    readonly components: readonly ComponentDescription[] | undefined;
}

export type UpdateDescriptionFunc = (updates: Partial<Description>) => void;
export type UpdateScreenDescriptionFunc = <T extends ScreenDescription>(updates: Partial<T>) => void;

export enum ScreenDescriptionKind {
    Class = "class",
    Array = "array",
    Chat = "chat",
    ShoppingCart = "shopping-cart",
}

export enum SpecialValueKind {
    Timestamp = "timestamp",
    VerifiedEmailAddress = "verified-email-address",
    RealEmailAddress = "real-email-address",
    UniqueIdentifier = "unique-identifier",
    ActionSource = "action-source",
    CurrentURL = "current-url",
    ClearColumn = "clear-column",
}

export const specialValueTypeKinds: {
    [k in SpecialValueKind]: PrimitiveGlideTypeKind;
} = {
    [SpecialValueKind.Timestamp]: "date-time",
    [SpecialValueKind.VerifiedEmailAddress]: "email-address",
    [SpecialValueKind.RealEmailAddress]: "email-address",
    [SpecialValueKind.UniqueIdentifier]: "string",
    [SpecialValueKind.ActionSource]: "string",
    [SpecialValueKind.CurrentURL]: "uri",
    [SpecialValueKind.ClearColumn]: "string",
};

export const paymentProcessorCodec = iots.keyof({ stripe: null });
export type PaymentProcessor = iots.TypeOf<typeof paymentProcessorCodec>;

export interface PaymentMethod {
    readonly processor: PaymentProcessor;
}

export function isPaymentMethod(x: unknown): x is PaymentMethod {
    return typeof x === "object" && hasOwnProperty(x, "processor");
}

export const defaultPaymentMethod: PaymentMethod = { processor: "stripe" };

export enum UnaryPredicateFormulaOperator {
    IsNotEmpty = "is-not-empty",
}

export enum UnaryPredicateCompositeOperator {
    IsEmpty = "is-empty",
    MatchesVerifiedEmailAddress = "matches-verified-email-address",
    DoesNotMatchVerifiedEmailAddress = "does-not-match-verified-email-address",
}

export type UnaryPredicateOperator = UnaryPredicateFormulaOperator | UnaryPredicateCompositeOperator;

export enum BinaryPredicateFormulaOperator {
    Equals = "equals",
    ContainsString = "contains-string",
    IsContainedInString = "is-contained-in-string",
    IsLessThan = "is-less-than",
    IsGreaterThan = "is-greater-than",
    IsLessOrEqualTo = "is-less-or-equal-to",
    IsGreaterOrEqualTo = "is-greater-or-equal-to",
    MatchesEmailAddress = "matches-email-address",
}

export enum BinaryPredicateCompositeOperator {
    DoesNotEqual = "does-not-equal",
    DoesNotContainString = "does-not-contain-string",
    IsNotContainedInString = "is-not-contained-in-string",
    IsBefore = "is-before",
    IsAfter = "is-after",
    IsOnOrBefore = "is-on-or-before",
    IsOnOrAfter = "is-on-or-after",
    IsWithin = "is-within",
}

export type BinaryPredicateOperator = BinaryPredicateFormulaOperator | BinaryPredicateCompositeOperator;

export type PredicateOperator = UnaryPredicateOperator | BinaryPredicateOperator;

export function isMatchEmailOperator(op: PredicateOperator): boolean {
    return (
        op === UnaryPredicateCompositeOperator.MatchesVerifiedEmailAddress ||
        op === UnaryPredicateCompositeOperator.DoesNotMatchVerifiedEmailAddress
    );
}

export interface ArrayFilter {
    // In an email filter, `key` is the email special value, and `value` is
    // the column to be matched.  In a simple reference filter, `key` is the
    // column in the table where the reference lives, and `value` is the
    // column in the target table.
    readonly key: PropertyDescription;
    readonly operator: BinaryPredicateFormulaOperator.Equals;
    readonly value: PropertyDescription;
}

export function isEmailArrayFilter(filter: ArrayFilter): boolean {
    return filter.key.kind === PropertyKind.SpecialValue && filter.key.value === SpecialValueKind.VerifiedEmailAddress;
}

export interface TransformableContentDescription {
    // FIXME: This is legacy and shouldn't be in use anymore - remove
    // eventually.
    readonly filter?: LegacyPropertyDescription;

    readonly transforms: readonly ArrayTransform[] | undefined;
}

export enum ArrayTransformKind {
    Filter = "filter",
    Sort = "sort",
    Shuffle = "shuffle",
    TableOrder = "table-order",
    Limit = "limit",
}

export type OrderingArrayTransform = SortArrayTransform | TableOrderArrayTransform | ShuffleArrayTransform;

function isOrderingArrayTransform(t: ArrayTransform): t is OrderingArrayTransform {
    return (
        t.kind === ArrayTransformKind.Sort ||
        t.kind === ArrayTransformKind.Shuffle ||
        t.kind === ArrayTransformKind.TableOrder
    );
}

export function filterOrderingArrayTransforms(
    transforms: readonly ArrayTransform[]
): readonly OrderingArrayTransform[] {
    return transforms.filter(isOrderingArrayTransform) as OrderingArrayTransform[];
}

export function findFilterArrayTransform(transforms: readonly ArrayTransform[]): FilterArrayTransform | undefined {
    const filterTransforms = transforms.filter(t => t.kind === ArrayTransformKind.Filter) as FilterArrayTransform[];
    if (filterTransforms.length === 0) return undefined;
    if (filterTransforms.length > 1) {
        return panic("More than one filter transform");
    }
    return filterTransforms[0];
}

export function withDefaultOrderingArrayTransform<T extends ArrayTransform>(
    transforms: readonly T[]
): readonly (T | OrderingArrayTransform)[] {
    if (filterOrderingArrayTransforms(transforms).length === 0) {
        return [...transforms, { kind: ArrayTransformKind.TableOrder, reverse: false }];
    } else {
        return transforms;
    }
}

export function getArrayTransforms(
    desc: TransformableContentDescription,
    withOrder: boolean = true,
    withLimit: boolean = true
): readonly ArrayTransform[] {
    let transforms = withDefault(desc.transforms, []);
    if (withOrder) {
        transforms = withDefaultOrderingArrayTransform(transforms);
    } else {
        transforms = transforms.filter(t => !isOrderingArrayTransform(t));
    }
    if (!withLimit) {
        transforms = transforms.filter(t => t.kind !== ArrayTransformKind.Limit);
    }
    return transforms;
}

export interface ClassScreenDescription extends TransformableContentDescription {
    readonly kind: ScreenDescriptionKind.Class;
    readonly type: TableRefGlideType;

    readonly canEdit: boolean | undefined;
    readonly canEditFilters: readonly ArrayTransform[] | undefined;

    readonly canDelete: boolean | undefined;
    readonly canDeleteFilters: readonly ArrayTransform[] | undefined;

    readonly searchPlaceholder: PropertyDescription | undefined;
    readonly isForm: boolean | undefined;
    readonly fetchesData: boolean | undefined; // defaults to `false`
    readonly title: PropertyDescription | undefined;

    readonly components: ReadonlyArray<ComponentDescription>;
}

export interface LegacyColumnAssignments {
    [destColumn: string]: PropertyDescription;
}

export interface ColumnAssignment {
    readonly destColumn: string;
    readonly value: PropertyDescription;
}

export interface ColumnAssignmentsDescription {
    readonly columnAssignments?: readonly ColumnAssignment[];
}

export interface ColumnAssignmentsWithLegacyDescription extends ColumnAssignmentsDescription {
    readonly columns?: LegacyColumnAssignments;
}

// Add screens are of this type, too.
export interface EditScreenDescription extends ClassScreenDescription, ColumnAssignmentsWithLegacyDescription {
    readonly onSubmitActions: readonly ActionDescription[] | undefined;
}

export interface FormScreenDescription extends EditScreenDescription, ColumnAssignmentsWithLegacyDescription {
    readonly isForm: true;

    readonly formType: TableRefGlideType;
}

export enum ArrayScreenFormat {
    List = "list",
    Grid = "grid",
    CalendarList = "calendar-list",
    Columns = "columns",
    Gallery = "gallery",
    Map = "map-list",
    SmallList = "small-list",
    CheckList = "check-list",
    Tiles = "tiles",
    Cards = "cards",
    PieChart = "pie-chart",
    Choice = "choice",
    EventPicker = "event-picker",
    Tinder = "tinder",
    CardCollection = "card-collection",
    DataGrid = "data-grid",
}

export type ArrayPivot = {
    readonly filter?: ArrayFilter;
} & (
    | {
          readonly titleLocalizedStringKey: LocalizedStringKey;
          readonly title: undefined;
      }
    | {
          readonly titleLocalizedStringKey: undefined;
          readonly title: string;
      }
);

export interface FilterArrayTransform {
    readonly kind: ArrayTransformKind.Filter;
    readonly predicate: Formula;
    readonly isActive: boolean | undefined; // true by default
}

export enum SortOrder {
    Ascending = "asc",
    Descending = "desc",
}

interface SortArrayKey {
    readonly key: Formula;
    readonly order: SortOrder;
}

export interface SortArrayTransform {
    readonly kind: ArrayTransformKind.Sort;
    readonly keys: readonly SortArrayKey[];
}

export interface ShuffleArrayTransform {
    readonly kind: ArrayTransformKind.Shuffle;
}

export interface TableOrderArrayTransform {
    readonly kind: ArrayTransformKind.TableOrder;
    readonly reverse: boolean;
}

export interface LimitArrayTransform {
    readonly kind: ArrayTransformKind.Limit;
    readonly numRows: PropertyDescription;
}

export type ArrayTransform =
    | FilterArrayTransform
    | SortArrayTransform
    | ShuffleArrayTransform
    | TableOrderArrayTransform
    | LimitArrayTransform;

export interface DynamicSortColumn {
    readonly column: PropertyDescription;
}

export interface ArrayContentDescription extends DescriptionWithAction, TransformableContentDescription {
    // FIXME: This is legacy and fixed in `fixApp` - remove eventually
    readonly reverse: PropertyDescription | undefined;

    readonly limit: PropertyDescription | undefined;
    readonly groupByColumn: PropertyDescription | undefined;
    readonly components: readonly ComponentDescription[] | undefined;
}

// We don't have primitive array screens anymore, but we still need to
// have this type here so as to not crash old apps.
export type ArrayScreenItemType = PrimitiveGlideType | TableRefGlideType;

export interface ArrayScreenDescription extends ArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly type: ArrayScreenItemType;

    // FIXME: Why is this not part of `ArrayContentDescription`?  In
    // `InlineListComponentDescription` this is a `PropertyDescription`.
    readonly format: ArrayScreenFormat;
    readonly fetchesData: boolean | undefined;
    readonly search?: LegacyPropertyDescription;
    readonly searchPlaceholder: PropertyDescription | undefined;

    readonly canAddRow: PropertyDescription | undefined;
    readonly canAddRowFilters: readonly ArrayTransform[] | undefined;

    readonly pivots?: ReadonlyArray<ArrayPivot>;
    readonly dynamicFilterColumn: PropertyDescription | undefined;
    readonly dynamicSortColumns: PropertyDescription | undefined;
}

export type ClassOrArrayScreenDescription = ClassScreenDescription | ArrayScreenDescription;

export function isClassOrArrayScreenDescription(
    desc: ScreenDescription | undefined
): desc is ClassOrArrayScreenDescription {
    return desc?.kind === ScreenDescriptionKind.Class || desc?.kind === ScreenDescriptionKind.Array;
}

// In almost all cases you'll want to use ##getScreenComponents instead of
// this.
export function getScreenComponentsArray(
    desc: ClassScreenDescription | ArrayContentDescription
): readonly ComponentDescription[] {
    return desc.components ?? [];
}

// ##getScreenComponents:
// This will return the components in the screen
// description only if the screen actually supports components.
export function getScreenComponents(desc: ClassOrArrayScreenDescription): readonly ComponentDescription[] {
    if (desc.kind === ScreenDescriptionKind.Class) {
        return getScreenComponentsArray(desc);
    } else if (desc.kind === ScreenDescriptionKind.Array) {
        // FIXME: Don't hardcode Tinder here, but use the array screen handler
        // instead.  That needs a bit more dependency distentanglement,
        // though.
        if (desc.format === ArrayScreenFormat.Tinder) {
            return getScreenComponentsArray(desc);
        } else {
            return [];
        }
    } else {
        return assertNever(desc);
    }
}

export interface SpanColumn<T> {
    readonly width: number;
    readonly components: readonly { readonly ui: T; readonly id: string }[];
}
export type Span<T> = readonly SpanColumn<T>[];
export type ComponentLayout<T> = readonly Span<T>[];

export function getStructureLayoutForComponents(
    components: readonly ComponentDescription[]
): ComponentLayout<ComponentDescription> {
    const mapped = Array.from(getSpanCoordsForComponents(components));
    const sorted = mapped.sort(([, [aSpan, aCol, aInd]], [, [bSpan, bCol, bInd]]) => {
        if (aSpan - bSpan !== 0) return aSpan - bSpan;
        if (aCol - bCol !== 0) return aCol - bCol;
        return aInd - bInd;
    });

    let lastSpan: number | undefined;
    let lastCol: number | undefined;
    let curCol: Mutable<SpanColumn<ComponentDescription>> = {
        width: 0,
        components: [],
    };
    let curSpan: Mutable<Span<ComponentDescription>> = [curCol];
    const result: Mutable<ComponentLayout<ComponentDescription>> = [curSpan];
    for (const [c, loc] of sorted) {
        const [span, col] = loc;
        if (lastCol === undefined) lastCol = col;
        if (lastSpan === undefined) lastSpan = span;

        if (lastSpan !== span) {
            curSpan = [(curCol = { width: 0, components: [] })];
            result.push(curSpan);
            lastSpan = span;
            lastCol = col;
        } else if (lastCol !== col) {
            curCol = { width: 0, components: [] };
            curSpan.push(curCol);
            lastCol = col;
        }
        (curCol.components as any[]).push(c);
    }

    for (const span of result) {
        const numCols = Math.min(4, span.length);
        const width = Math.floor(12 / numCols);
        for (const col of span) {
            (col as any).width = width;
        }
    }

    return result;
}

export function getSpanCoordsForComponents(
    components: readonly ComponentDescription[]
): readonly [ComponentDescription, SpanCoordsFloat][] {
    let maxSpan = Math.max(0, ...components.map(c => c.spanCoordsFloat?.[0] ?? 0));
    return components.map(c => {
        let { spanCoordsFloat } = c;
        if (spanCoordsFloat === undefined) {
            maxSpan += 128;
            spanCoordsFloat = [maxSpan, 128, 128];
        }
        return [c, spanCoordsFloat];
    });
}

export interface ChatScreenDescription {
    readonly kind: ScreenDescriptionKind.Chat;
}

export interface ShoppingCartScreenDescription {
    readonly kind: ScreenDescriptionKind.ShoppingCart;
}

export type ScreenDescription =
    | ClassScreenDescription
    | FormScreenDescription
    | ArrayScreenDescription
    | ChatScreenDescription
    | ShoppingCartScreenDescription;

// FIXME: implement validation
export const screenDescriptionCodec = new iots.Type<ScreenDescription>(
    "ScreenDescription",
    (_u): _u is ScreenDescription => true,
    i => iots.success(i as ScreenDescription),
    a => a
);

// FIXME: implement validation
export const propertyDescriptionCodec = new iots.Type<PropertyDescription>(
    "PropertyDescription",
    (_u): _u is PropertyDescription => true,
    i => iots.success(i as PropertyDescription),
    a => a
);

export const userProfileDescriptionCodec = iots.type({
    userProfileTable: propertyDescriptionCodec,
    emailColumn: propertyDescriptionCodec,
    nameColumn: propertyDescriptionCodec,
    imageColumn: propertyDescriptionCodec,
    allowImageUpload: propertyDescriptionCodec,
    // FIXME: Let the app creator set this somewhere
    rolesColumn: iots.union([propertyDescriptionCodec, iots.undefined]),
});
export type UserProfileDescription = iots.TypeOf<typeof userProfileDescriptionCodec>;

const tabAppearance = iots.intersection([
    iots.type({
        icon: iots.string,
        hidden: iots.boolean,
    }),
    iots.partial({
        title: iots.string,
        inFlyout: iots.boolean,
        // These are only used in Pages:
        slug: iots.string,
        showInMenu: iots.boolean,
    }),
]);
export type TabAppearance = iots.TypeOf<typeof tabAppearance>;

// FIXME: implement validation
const stringLegacyPropertyDescription = new iots.Type<LegacyPropertyDescription>(
    "LegacyPropertyDescription",
    (_u): _u is LegacyPropertyDescription => true,
    i => iots.success(i as LegacyPropertyDescription),
    a => a
);

// FIXME: implement validation
const arrayTransformCodec = new iots.Type<ArrayTransform>(
    "ArrayTransform",
    (_u): _u is ArrayTransform => true,
    i => iots.success(i as ArrayTransform),
    a => a
);

export const tabDescriptionCodec = iots.intersection([
    tabAppearance,
    iots.type({
        screenName: stringLegacyPropertyDescription,
    }),
    iots.partial({
        // If `propertyName` is undefined, the tab is for the root
        // context.
        propertyName: iots.string,
        visibilityFilters: iots.readonlyArray(arrayTransformCodec),
    }),
]);
export type TabDescription = iots.TypeOf<typeof tabDescriptionCodec>;

export function makeTableRef(tableOrTableName: TableGlideType | TableName): TableRefGlideType {
    return { kind: "table-ref", tableName: getTableName(tableOrTableName) };
}

export function makeNativeTableRef(tableName: NativeTableName): NativeTableRefGlideType {
    return { kind: "table-ref", tableName };
}

export function makeUniversalTableRef(tableName: UniversalTableName): UniversalTableRefGlideType {
    if (isTableName(tableName)) {
        return makeTableRef(tableName);
    } else if (isNativeTableName(tableName)) {
        return makeNativeTableRef(tableName);
    } else {
        return assertNever(tableName);
    }
}

type TableOrColumns = TableGlideType | readonly TableColumn[];

function getColumns(tableOrColumns: TableOrColumns): readonly TableColumn[] {
    return isArray(tableOrColumns) ? tableOrColumns : tableOrColumns.columns;
}

export function getTableColumn(tableOrColumns: TableOrColumns, name: string): TableColumn | undefined {
    return getColumns(tableOrColumns).find(c => c.name === name);
}

export function isColumnNonHidden(c: TableColumn, allowProtected: boolean = true): boolean {
    return c.hidden !== true && (allowProtected || c.isProtected !== true);
}

export function getNonHiddenColumns(
    tableOrColumns: TableOrColumns,
    allowProtected: boolean = true
): readonly TableColumn[] {
    return getColumns(tableOrColumns).filter(c => isColumnNonHidden(c, allowProtected));
}

export function isColumnWriteable(
    c: TableColumn,
    table: TableGlideType,
    allowUserSpecific: boolean,
    allowProtected: boolean
): boolean {
    return (
        table.isReadOnly !== true &&
        c.isReadOnly !== true &&
        isPrimitiveType(c.type) &&
        isColumnNonHidden(c) &&
        (allowUserSpecific || c.isUserSpecific !== true) &&
        (allowProtected || c.isProtected !== true) &&
        !isComputedColumn(c) &&
        c.name !== table.rowIDColumn
    );
}

export function getPrimitiveColumns(t: TableGlideType): ReadonlyArray<TableColumn> {
    return t.columns.filter(c => isPrimitiveType(c.type));
}

export enum MutatingScreenKind {
    AddScreen,
    EditScreen,
    FormScreen,
}

export function hasInputContext(kind: MutatingScreenKind | undefined): boolean {
    return kind === undefined || kind === MutatingScreenKind.FormScreen || kind === MutatingScreenKind.EditScreen;
}

export function allowProtectedColumns(
    isEditedInApp: boolean | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined
): boolean {
    if (isEditedInApp !== true) return false;
    return mutatingScreenKind === MutatingScreenKind.AddScreen || mutatingScreenKind === MutatingScreenKind.FormScreen;
}

export function filterEditableColumns(
    table: TableGlideType,
    isNativeTable: boolean,
    columns: readonly TableColumn[],
    mutatingScreenKind: MutatingScreenKind | undefined
): readonly TableColumn[] {
    const forDetailOrEditScreen =
        mutatingScreenKind === undefined || mutatingScreenKind === MutatingScreenKind.EditScreen;
    const allowUserSpecific = forDetailOrEditScreen || isNativeTable;
    const allowProtected = allowProtectedColumns(true, mutatingScreenKind);
    return columns.filter(c => isColumnWriteable(c, table, allowUserSpecific, allowProtected));
}

export function getPrimitiveNonHiddenColumns(t: TableGlideType): ReadonlyArray<TableColumn> {
    return getNonHiddenColumns(getPrimitiveColumns(t));
}

export function getUserVisibleReadablePrimitiveColumns(
    t: TableGlideType,
    withFavorite: boolean
): readonly TableColumn[] {
    let columns = getPrimitiveNonHiddenColumns(t);
    if (!withFavorite) {
        columns = columns.filter(c => c.name !== isFavoritedColumnName);
    }
    return columns;
}

export function getCompoundColumns(t: TableGlideType): readonly TableColumn[] {
    return t.columns.filter(c => !isPrimitiveType(c.type));
}

// If `forWriting` is `true` then only tables with writable primitives
// columns are returned.
export function getTablesWithPrimitiveColumns(
    tables: ReadonlyArray<TableGlideType>,
    forWriting: boolean
): readonly TableGlideType[] {
    return tables.filter(t => {
        if (getTableName(t).isSpecial) return false;
        if (forWriting && t.isReadOnly === true) return false;
        return t.columns.some(c => isPrimitiveType(c.type) && (forWriting || isColumnWriteable(c, t, true, true)));
    });
}

export function getStringTypeOrStringTypeArrayColumns(table: TableGlideType): TableColumn[] {
    return table.columns.filter(c => isStringTypeOrStringTypeArray(c.type));
}

export function getPrimitiveArrayNonHiddenColumns(t: TableGlideType): readonly TableColumn[] {
    return t.columns.filter(c => c.hidden !== true && c.type.kind === "array" && isPrimitiveType(c.type.items));
}

export function getPrimitiveOrPrimitiveArrayNonHiddenColumns(t: TableGlideType): readonly TableColumn[] {
    return t.columns.filter(
        c =>
            c.hidden !== true && (isPrimitiveType(c.type) || (c.type.kind === "array" && isPrimitiveType(c.type.items)))
    );
}

export function getBackendCompatiblePrimitiveColumns(table: TableGlideType): readonly TableColumn[] {
    return getPrimitiveNonHiddenColumns(table).filter(c => !isComputedColumn(c));
}

export function areContextColumnEditsInAddedRow(mutatingScreenKind: MutatingScreenKind | undefined): boolean {
    return mutatingScreenKind === MutatingScreenKind.AddScreen || mutatingScreenKind === MutatingScreenKind.FormScreen;
}

export function isFormScreen(desc: ScreenDescription | undefined): desc is FormScreenDescription {
    if (desc?.kind !== ScreenDescriptionKind.Class) return false;
    return desc.isForm === true;
}

export function getAllowedTablesForAddRow(schema: TypeSchema): ReadonlyArray<TableGlideType> {
    return getTablesWithPrimitiveColumns(schema.tables, true);
}

export function findTable(
    schemaOrTables: TypeSchema | readonly TableGlideType[],
    nameOrRef: TableName | TableRefGlideType | undefined
): TableGlideType | undefined;
export function findTable(
    schemaOrTables: TypeSchema | readonly TableGlideType[],
    nameOrRef: UniversalTableName | UniversalTableRefGlideType | undefined,
    sourceMetadata: readonly SourceMetadata[] | undefined
): TableGlideType | undefined;
export function findTable(
    schemaOrTables: TypeSchema | readonly TableGlideType[],
    nameOrRef: UniversalTableName | UniversalTableRefGlideType | undefined,
    sourceMetadata?: readonly SourceMetadata[] | undefined
): TableGlideType | undefined {
    if (nameOrRef === undefined) return undefined;
    let name = getTableRefTableName(nameOrRef);
    if (isNativeTableName(name)) {
        if (sourceMetadata === undefined) return undefined;
        const sm = getSourceMetadataForNativeTable(sourceMetadata, name.nativeTableID);
        if (sm === undefined) return undefined;
        name = sm.tableName;
    }
    const tables = isArray(schemaOrTables) ? schemaOrTables : schemaOrTables.tables;
    return tables.find(t => areTableNamesEqual(name, getTableName(t)));
}

export enum GlobalVariableNames {
    VerifiedEmailAddress = "verified-email-address",
    RealEmailAddress = "real-email-address",
    UserProfileTableName = "user-profile-table-name",
    UserProfileEmailColumnName = "user-profile-email-column-name",
    UserProfileNameColumnName = "user-profile-name-column-name",
    UserProfileImageColumnName = "user-profile-image-column-name",
    AppFeatures = "app-features",
    AppMetadata = "app-metadata",
}
